import { useState, useCallback } from "react";
import {
  ArrowRight, LogOut, ArrowLeft, ExternalLink, KeyRound, X, Home,
  Compass, Paintbrush, Megaphone, BookOpen, Briefcase,
  Users, ShoppingBag, Handshake, Send, Inbox, Globe, Download,
} from "lucide-react";
import Marquee from "react-fast-marquee";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_INTERESTS = [
  "Film production", "Video production", "Podcast production", "Branded content production",
  "Original IP development", "Publishing (Ink + Print)", "Script development or pitch support",
  "Social media setup or strategy", "AI website build package", "Creative consulting",
  "Build your brand resources", "Build your business resources",
  "Creative Ensemble community membership", "Aussie's Guide collaboration or travel features",
  "Sponsorship opportunities", "Collaboration opportunities", "Partnership or brand collaboration",
];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

/* ═══════════════ SECTION CONTENT ═══════════════ */

const DISCOVER_TILES = [
  {
    id: "submit",
    icon: Send,
    title: "Submit",
    subtitle: "Pitches, scripts & treatments",
    description:
      "This is the front door for new ideas. We accept children's book pitches, scripts, scripted and unscripted treatments, podcast concepts, and original story submissions for review by the Creative Ensemble Media team. If you have something to say, send it in — we read everything.",
    items: [
      "Children's book pitches",
      "Film & TV scripts",
      "Scripted treatments",
      "Unscripted treatments",
      "Podcast concepts",
      "Original story submissions",
    ],
    ctas: ["Submit a Pitch"],
  },
  {
    id: "acquisitions",
    icon: Inbox,
    title: "Acquisitions",
    subtitle: "Finished or partially finished projects",
    description:
      "Acquisitions is for finished or partially finished projects looking for a home — completed manuscripts, recorded podcasts, edited shorts, partially produced series, and original IP ready to scale. If your project is past the idea stage, this is where you bring it. (This includes everything that used to live under Production Inquiries.)",
    items: [
      "Completed manuscripts & books",
      "Recorded podcast series",
      "Edited shorts & films",
      "Partially produced series",
      "Original IP ready to license",
      "Branded content in progress",
    ],
    ctas: ["Submit for Acquisition", "Book a Consultation"],
  },
];

const CREATE_TILES = [
  {
    id: "build-your-brand",
    icon: Briefcase,
    title: "Build Your Brand",
    subtitle: "Resources, strategy & creative direction",
    description:
      "Work with Creative Ensemble Media to develop your brand voice, visual identity, and creative direction. Below are a few example resources from our team — and you can always grab free templates from our Resources library.",
    items: [
      "Brand development & positioning",
      "Creative strategy",
      "Social media packages",
      "Pitch & deck preparation",
      "Creative consulting sessions",
    ],
    ctas: ["Explore Studio Services"],
    special: "build-your-brand",
  },
  {
    id: "partnerships",
    icon: Handshake,
    title: "Partnerships & Collaborations",
    subtitle: "Sponsorships, editorial & brand work",
    description:
      "Collaborate with Creative Ensemble Media on sponsorships, brand partnerships, editorial features, events, and creative co-productions. Gain access to our 25K+ multi-platform audience across travel, lifestyle, and creative communities.",
    items: [
      "Brand sponsorships",
      "Event sponsorships",
      "Editorial partnerships",
      "Travel partnerships",
      "Production partnerships",
      "Artist collaborations",
    ],
    ctas: ["Submit Inquiry", "Start a Conversation"],
  },
];

const AMPLIFY_TILES = [
  {
    id: "ink-print",
    icon: BookOpen,
    title: "Ink + Print",
    subtitle: "Publishing & distribution",
    description:
      "The publishing arm of Creative Ensemble Media — original releases, printable books, editorial projects, and studio resources distributed across platforms.",
    items: [
      "Printable books",
      "The Adventures of Clancy Pants",
      "Creative worksheets",
      "Digital editorial releases",
      "Etsy store",
      "Gumroad resources",
    ],
    ctas: ["Browse the Library", "Explore Shop"],
  },
  {
    id: "our-brands",
    icon: Globe,
    title: "Our Brands",
    subtitle: "Where we amplify your message",
    description:
      "Our in-house brands carry your work across travel, lifestyle, kids, and creative audiences. Depending on the partnership, project, or collab, we amplify your message through one or more of these channels — each with its own voice, audience, and platform. Click any brand below to learn more.",
    ctas: [],
    special: "our-brands",
  },
];

/* Sample article cards for "Build Your Brand" */
const BRAND_ARTICLES = [
  { title: "How to Define Your Brand Voice", category: "Brand Strategy", color: "#FC4BAF" },
  { title: "5 Tips for a Strong Visual Identity", category: "Design", color: "#5170FF" },
  { title: "Building a Social Media Presence That Lasts", category: "Social", color: "#FFC2E8" },
  { title: "From Pitch to Platform: Telling Your Story", category: "Storytelling", color: "#FC4BAF" },
  { title: "Crafting a Brand That Stands Out", category: "Identity", color: "#5170FF" },
  { title: "The Creator's Guide to Consistency", category: "Strategy", color: "#FFC2E8" },
];

/* Brand tiles for "Our Brands" — external links open in NEW window */
const OUR_BRANDS = [
  { name: "The Adventures of Clancy Pants", tagline: "Children's book series",   color: "#FFC2E8", url: "#" },
  { name: "Satch and Co",                   tagline: "Lifestyle & design",       color: "#5170FF", url: "#" },
  { name: "Creative Aligned Era",           tagline: "Creative business community", color: "#FC4BAF", url: "#" },
  { name: "Ink + Print Store",              tagline: "Books & printable resources", color: "#000",    url: "#" },
  { name: "Aussie's Guide to LA",           tagline: "City travel guides",       color: "#FFC2E8", url: "#" },
  { name: "Aussie's Guide to NYC",          tagline: "City travel guides",       color: "#5170FF", url: "#" },
  { name: "Aussie's Guide to Europe",       tagline: "Travel features",          color: "#FC4BAF", url: "#" },
  { name: "Aussie's Guide to Sydney",       tagline: "Local travel guide",       color: "#000",    url: "#" },
  { name: "The Little Perfectionist",       tagline: "Editorial & lifestyle",    color: "#FFC2E8", url: "#" },
  { name: "The Sweater Dork",               tagline: "Style & culture",          color: "#5170FF", url: "#" },
];

/* Sample projects shown at the bottom of the DISCOVER page — uses real images from the main site */
const DISCOVER_PROJECTS = [
  { title: "The Travel Edit",      tag: "Editorial",   color: "#5170FF", img: "https://img1.wsimg.com/isteam/getty/2114448180/:/cr=t:0%25,l:10.1%25,w:79.8%25,h:100%25/rs=w:500" },
  { title: "She Speaks",           tag: "Podcast",     color: "#FC4BAF", img: "https://img1.wsimg.com/isteam/getty/1681036303/:/cr=t:0%25,l:5.75%25,w:88.5%25,h:100%25/rs=w:500" },
  { title: "The Creative Reel",    tag: "Film & TV",   color: "#FFC2E8", img: "https://img1.wsimg.com/isteam/getty/1428201539/:/cr=t:0%25,l:3.84%25,w:92.32%25,h:100%25/rs=w:500" },
  { title: "The Creative Ensemble", tag: "Community",  color: "#000",    img: "https://img1.wsimg.com/isteam/getty/1137694152/:/cr=t:0%25,l:5.67%25,w:88.67%25,h:100%25/rs=w:500" },
  { title: "Ink + Print",          tag: "Publishing",  color: "#5170FF", img: "https://img1.wsimg.com/isteam/getty/1149290599/:/cr=t:0%25,l:12.57%25,w:74.86%25,h:100%25/rs=w:500" },
  { title: "Ensemble Branded",     tag: "Branded",     color: "#FC4BAF", img: "https://img1.wsimg.com/isteam/getty/1828732247/:/cr=t:0%25,l:10.1%25,w:79.8%25,h:100%25/rs=w:500" },
];

/* Platforms we amplify across — shown on AMPLIFY page */
const AMPLIFY_PLATFORMS = [
  "YouTube", "TikTok", "Instagram", "Facebook", "Spotify",
  "Apple Podcasts", "Substack", "Etsy", "Gumroad", "Editorial Features",
];

const RESOURCES_TILE = {
  id: "resources",
  icon: Download,
  title: "Resources",
  subtitle: "Downloads & creative tools",
  description:
    "A growing library of downloadable guides, templates, worksheets, and creative tools from Creative Ensemble Media — built to help creators, founders, and storytellers move faster.",
  items: [
    "Ebooks & guides",
    "Creative worksheets",
    "Project planning templates",
    "Brand starter guides",
    "Social media templates",
    "Writing prompts",
  ],
  ctas: ["Browse Resources"],
};

/* ═══════════════ AUTH ═══════════════ */
export const HubAuth = ({ onAuth }) => {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", project_interest: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const update = useCallback((field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value })), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") {
        const { data } = await axios.post(`${API}/auth/register`, form, { withCredentials: true });
        onAuth(data);
      } else {
        const { data } = await axios.post(
          `${API}/auth/login`,
          { email: form.email, password: form.password },
          { withCredentials: true }
        );
        onAuth(data);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#000" }}>
      <div className="w-full max-w-md" style={{ background: "#5170FF", border: "3px solid #5170FF", boxShadow: "10px 10px 0px 0px #FC4BAF" }}>
        <div className="p-8">
          <h1 style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.5rem", textTransform: "uppercase", marginBottom: "1rem", color: "#fff" }}>
            Creative Ensemble Media Hub
          </h1>
          <p className="text-sm mb-6" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.8)" }}>
            Sign in to explore the studio, submit projects, and access member resources.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <input type="text" placeholder="Your Name" className="brutalist-input" value={form.name} onChange={update("name")} required data-testid="hub-register-name" />
            )}
            <input type="email" placeholder="Email" className="brutalist-input" value={form.email} onChange={update("email")} required data-testid="hub-email" />
            <input type="password" placeholder="Password" className="brutalist-input" value={form.password} onChange={update("password")} required data-testid="hub-password" />
            {mode === "register" && (
              <select className="brutalist-input" value={form.project_interest} onChange={update("project_interest")} data-testid="hub-project-interest">
                <option value="">What brings you here?</option>
                {PROJECT_INTERESTS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            {error && <p className="text-sm text-red-200 font-semibold" data-testid="hub-auth-error">{error}</p>}
            <button type="submit" className="brutalist-btn brutalist-btn-pink w-full" disabled={loading} data-testid="hub-auth-submit">
              {loading ? "..." : mode === "login" ? "Enter the Hub" : "Create Account"} <ArrowRight size={16} />
            </button>
          </form>
          <p className="text-sm text-center mt-6" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.7)" }}>
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} style={{ color: "#FFC2E8", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }} data-testid="hub-toggle-mode">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════ SHARED HEADER ═══════════════ */
const HubHeader = ({ onBack, backLabel = "Back" }) => (
  <header className="px-6 py-4 flex items-center justify-between" style={{ background: "#000", borderBottom: "3px solid #FC4BAF" }}>
    <span style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1rem", color: "#fff", textTransform: "uppercase" }}>
      <span style={{ color: "#FC4BAF" }}>Creative</span> <span style={{ color: "#5170FF" }}>Ensemble</span> <span style={{ color: "#FFC2E8" }}>Media</span> <span style={{ color: "#fff" }}>Hub</span>
    </span>
    {onBack && (
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider" style={{ fontFamily: "Outfit", color: "#FFC2E8", background: "none", border: "none", cursor: "pointer" }} data-testid="hub-back-btn">
        <ArrowLeft size={16} /> {backLabel}
      </button>
    )}
  </header>
);

/* ═══════════════ ROOM DETAIL BODY (used inside Shell) ═══════════════ */
const RoomDetailBody = ({ room, onResources }) => (
  <div className="px-6 py-12 md:py-16">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 flex items-center justify-center" style={{ background: "#FC4BAF" }}>
            <room.icon size={24} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "2rem", textTransform: "uppercase", color: "#fff" }}>{room.title}</h1>
            <p className="text-sm" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.6)" }}>{room.subtitle}</p>
          </div>
        </div>
        <p className="text-sm leading-relaxed mb-10 max-w-2xl" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.85)" }}>{room.description}</p>

        {/* SPECIAL: Build Your Brand → "Explore Studio Services" CTA inline (articles moved to bottom) */}
        {room.special === "build-your-brand" && (
          <div className="mb-8 flex flex-wrap gap-3">
            <button
              onClick={onResources}
              className="brutalist-btn brutalist-btn-pink"
              data-testid="brand-download-templates-btn"
            >
              <Download size={14} /> Download Free Templates
            </button>
          </div>
        )}

        {/* SPECIAL: Our Brands photo tiles linking out */}
        {room.special === "our-brands" && (
          <div className="mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold mb-4" style={{ fontFamily: "Outfit", color: "#FFC2E8" }}>
              Explore each brand
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="our-brands-grid">
              {OUR_BRANDS.map((b) => (
                <a
                  key={b.name}
                  href={b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-5 transition-all duration-200 hover:-translate-y-1 relative"
                  style={{ background: b.color, color: b.color === "#FFC2E8" ? "#000" : "#fff", textDecoration: "none" }}
                  data-testid={`our-brand-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <ExternalLink size={14} style={{ position: "absolute", top: 12, right: 12, opacity: 0.7 }} />
                  <div className="h-20 mb-3 flex items-center justify-center" style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)" }}>
                    <Globe size={28} style={{ opacity: 0.6 }} />
                  </div>
                  <h3 className="text-sm leading-tight mb-1" style={{ fontFamily: "Garet", fontWeight: 900, textTransform: "uppercase" }}>{b.name}</h3>
                  <p className="text-xs opacity-80" style={{ fontFamily: "Outfit" }}>{b.tagline}</p>
                </a>
              ))}
            </div>
            <p className="text-xs mt-4" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.4)" }}>
              Tip: replace placeholder links with your brand URLs to launch each tile in a new window.
            </p>
          </div>
        )}

        {/* Standard items list */}
        {room.items && room.items.length > 0 && (
          <div className="mb-10 p-6" style={{ background: "rgba(255,255,255,0.05)", border: "2px solid rgba(255,255,255,0.1)" }}>
            <p className="text-xs uppercase tracking-wider font-semibold mb-4" style={{ fontFamily: "Outfit", color: "#FFC2E8" }}>What's inside</p>
            <div className="grid md:grid-cols-2 gap-3">
              {room.items.map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm" style={{ fontFamily: "Outfit", color: "#fff" }}>
                  <span style={{ color: "#FFC2E8", fontWeight: 700 }}>+</span> {item}
                </div>
              ))}
            </div>
          </div>
        )}

        {room.ctas && room.ctas.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-10">
            {room.ctas.map((cta) => (
              <button key={cta} className="brutalist-btn brutalist-btn-pink" data-testid={`room-cta-${cta.toLowerCase().replace(/\s+/g, "-")}`}>
                {cta} <ArrowRight size={14} />
              </button>
            ))}
          </div>
        )}

        {/* SPECIAL: Build Your Brand articles/blog grid — ALWAYS at bottom */}
        {room.special === "build-your-brand" && (
          <div className="mt-12 pt-10" style={{ borderTop: "1px solid rgba(255,255,255,0.12)" }}>
            <h2 className="mb-2" style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.4rem", textTransform: "uppercase", color: "#FFC2E8" }}>
              From the Blog
            </h2>
            <p className="text-sm max-w-2xl mb-6" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.7)" }}>
              Featured articles, guides, and ideas from the Creative Ensemble Media studio team.
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="brand-articles-grid">
              {BRAND_ARTICLES.map((a) => (
                <div
                  key={a.title}
                  className="p-5 transition-all duration-200 hover:-translate-y-1"
                  style={{ background: a.color, cursor: "pointer", color: a.color === "#FFC2E8" ? "#000" : "#fff" }}
                  data-testid={`brand-article-${a.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p className="text-xs uppercase tracking-wider mb-2 opacity-70" style={{ fontFamily: "Outfit", fontWeight: 600 }}>{a.category}</p>
                  <h3 className="text-base leading-tight" style={{ fontFamily: "Garet", fontWeight: 900, textTransform: "uppercase" }}>{a.title}</h3>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
  </div>
);

/* ═══════════════ SECTION PAGE BODY (used inside Shell) ═══════════════ */
const SECTION_META = {
  discover: {
    title: "Discover",
    icon: Compass,
    tagline: "Where every project begins.",
    blurb:
      "Discover is the front door for new ideas — submit children's book pitches, scripts, scripted and unscripted treatments, podcast concepts, and original story submissions for review by our team. Or, if your project is already past the idea stage — completed manuscripts, recorded podcasts, edited shorts, partially produced series, or original IP ready to scale — bring it to Acquisitions and we'll find a home for it. Whether you're starting from a spark or finishing something extraordinary, this is where we take a serious look.",
    tiles: DISCOVER_TILES,
  },
  create: {
    title: "Create",
    icon: Paintbrush,
    tagline: "Where ideas become work.",
    blurb:
      "Create is where we step in alongside you — building brands, developing creative strategy, producing content, and shaping partnerships. Whether you're a creator looking to define your voice, a founder building a business, or a brand looking to collaborate with our 25K+ multi-platform audience, this is the workshop. Our ensemble brings film, video, podcast, editorial, social, and AI-built website packages under one roof.",
    tiles: CREATE_TILES,
  },
  amplify: {
    title: "Amplify",
    icon: Megaphone,
    tagline: "Where your work travels.",
    blurb:
      "Amplify is the engine that takes finished work — your story, your project, your brand — and pushes it across platforms and audiences. We don't just publish; we distribute through Ink + Print, our 14+ in-house brands, and a multi-platform footprint reaching travel, lifestyle, kids, and creative communities. Depending on your partnership, project, or collab, we tailor the amplification path that fits your message — and we're great at it.",
    tiles: AMPLIFY_TILES,
  },
};

const SectionPageBody = ({ sectionKey, onSelectRoom }) => {
  const meta = SECTION_META[sectionKey];
  const Icon = meta.icon;
  return (
    <div className="px-6 py-12 md:py-16">
      <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 flex items-center justify-center" style={{ background: "#5170FF" }}>
              <Icon size={26} color="#fff" />
            </div>
            <h1 style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "clamp(2rem, 5vw, 3rem)", textTransform: "uppercase", color: "#fff" }} data-testid={`section-title-${sectionKey}`}>
              {meta.title}
            </h1>
          </div>
          <p className="mb-4 max-w-2xl" style={{ fontFamily: "Outfit", fontSize: "1.05rem", fontWeight: 600, color: "#fff" }} data-testid={`section-tagline-${sectionKey}`}>
            {meta.tagline}
          </p>
          <p className="text-sm max-w-2xl mb-12 leading-relaxed" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.75)" }}>
            {meta.blurb}
          </p>

          <div className={`grid gap-4 ${meta.tiles.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
            {meta.tiles.map((room) => (
              <button
                key={room.id}
                onClick={() => onSelectRoom(room)}
                className="text-left p-6 transition-all duration-200 hover:-translate-y-1"
                style={{ background: "#FC4BAF", border: "none", cursor: "pointer", minHeight: "180px" }}
                data-testid={`section-tile-${room.id}`}
              >
                <room.icon size={20} color="#FFC2E8" className="mb-3" />
                <h3 className="text-lg mb-2" style={{ fontFamily: "Garet", fontWeight: 900, textTransform: "uppercase", color: "#fff" }}>
                  {room.title}
                </h3>
                <p className="text-xs mb-3" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.8)" }}>
                  {room.subtitle}
                </p>
                <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider" style={{ fontFamily: "Outfit", color: "#FFC2E8" }}>
                  Open <ArrowRight size={12} />
                </span>
              </button>
            ))}
          </div>

          {/* DISCOVER bottom: featured projects */}
          {sectionKey === "discover" && (
            <div className="mt-16" data-testid="discover-projects-section">
              <h2 className="mb-2" style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.4rem", textTransform: "uppercase", color: "#FFC2E8" }}>
                Check Out Some of Our Projects
              </h2>
              <p className="text-sm max-w-2xl mb-6" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.7)" }}>
                A glimpse of what's already moved through Discover — from podcasts and editorial series to children's books and travel features.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {DISCOVER_PROJECTS.map((p) => (
                  <div
                    key={p.title}
                    className="overflow-hidden transition-all duration-200 hover:-translate-y-1"
                    style={{ background: p.color, color: p.color === "#FFC2E8" ? "#000" : "#fff", cursor: "pointer" }}
                    data-testid={`discover-project-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="w-full" style={{ aspectRatio: "16 / 10", overflow: "hidden", background: "#000" }}>
                      <img src={p.img} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="p-4">
                      <p className="text-xs uppercase tracking-wider mb-1 opacity-70" style={{ fontFamily: "Outfit", fontWeight: 600 }}>{p.tag}</p>
                      <h3 className="text-base leading-tight" style={{ fontFamily: "Garet", fontWeight: 900, textTransform: "uppercase" }}>{p.title}</h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AMPLIFY bottom: platform reach */}
          {sectionKey === "amplify" && (
            <div className="mt-16" data-testid="amplify-platforms-section">
              <h2 className="mb-2" style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.4rem", textTransform: "uppercase", color: "#FFC2E8" }}>
                Where We Amplify
              </h2>
              <p className="text-sm max-w-2xl mb-6" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.7)" }}>
                We're great at this — across 14+ in-house brands and a 25K+ audience, we move stories through every platform that matters. Your work doesn't sit. It travels.
              </p>
              <div className="flex flex-wrap gap-2">
                {AMPLIFY_PLATFORMS.map((p, i) => {
                  const palette = ["#FC4BAF", "#5170FF", "#FFC2E8"];
                  const bg = palette[i % palette.length];
                  return (
                    <span
                      key={p}
                      className="px-4 py-2 text-xs uppercase tracking-wider"
                      style={{ background: bg, color: bg === "#FFC2E8" ? "#000" : "#fff", fontFamily: "Outfit", fontWeight: 700 }}
                      data-testid={`amplify-platform-${p.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {p}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

/* ═══════════════ TOP DROPDOWN NAV ═══════════════ */
const TopNav = ({ onSection, onRoom, onResources }) => {
  const [open, setOpen] = useState(null);
  const items = [
    { key: "discover", label: "Discover", tiles: DISCOVER_TILES },
    { key: "create",   label: "Create",   tiles: CREATE_TILES },
    { key: "amplify",  label: "Amplify",  tiles: AMPLIFY_TILES },
  ];
  return (
    <div className="px-6 py-3 flex gap-2 justify-center flex-wrap relative z-40" style={{ background: "#111", borderBottom: "1px solid #222" }}>
      {items.map((it) => {
        const isOpen = open === it.key;
        return (
          <div
            key={it.key}
            className="relative"
            onMouseEnter={() => setOpen(it.key)}
            onMouseLeave={() => setOpen(null)}
          >
            <button
              onClick={() => { onSection(it.key); setOpen(null); }}
              className="whitespace-nowrap text-xs uppercase tracking-wider font-semibold px-4 py-2 transition-colors"
              style={{ fontFamily: "Outfit", color: isOpen ? "#FFC2E8" : "#5170FF", background: "none", border: "none", cursor: "pointer" }}
              data-testid={`topnav-${it.key}`}
            >
              {it.label}
            </button>
            {isOpen && (
              <div
                className="absolute top-full left-0 pt-1 min-w-[220px] z-50"
                data-testid={`topnav-${it.key}-dropdown`}
              >
                <div className="py-2" style={{ background: "#111", border: "2px solid #333" }}>
                  {it.tiles.map((room) => (
                    <button
                      key={room.id}
                      onClick={() => { onRoom(room); setOpen(null); }}
                      className="block w-full text-left px-4 py-2 text-xs hover:bg-white/5 transition-colors"
                      style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.85)", background: "none", border: "none", cursor: "pointer" }}
                      data-testid={`topnav-${it.key}-${room.id}`}
                    >
                      {room.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
      <button
        onClick={onResources}
        className="whitespace-nowrap text-xs uppercase tracking-wider font-semibold px-4 py-2 transition-colors"
        style={{ fontFamily: "Outfit", color: "#FFC2E8", background: "none", border: "none", cursor: "pointer" }}
        data-testid="topnav-resources"
      >
        Resources
      </button>
    </div>
  );
};

/* ═══════════════ CHANGE PASSWORD MODAL ═══════════════ */
const ChangePasswordModal = ({ onClose }) => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const update = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(false);
    if (form.new_password.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (form.new_password !== form.confirm_password) { setError("New passwords do not match."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: form.current_password,
        new_password: form.new_password,
      }, { withCredentials: true });
      setSuccess(true);
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-6"
      style={{ background: "rgba(0,0,0,0.85)" }}
      onClick={onClose}
      data-testid="change-password-modal"
    >
      <div
        className="w-full max-w-md relative"
        style={{ background: "#5170FF", border: "3px solid #5170FF", boxShadow: "10px 10px 0px 0px #FC4BAF" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-white/70 hover:text-white"
          style={{ background: "none", border: "none", cursor: "pointer" }}
          data-testid="change-password-close"
        >
          <X size={20} />
        </button>
        <div className="p-8">
          <h2 style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.4rem", textTransform: "uppercase", color: "#fff", marginBottom: "1rem" }}>
            Change Password
          </h2>
          <p className="text-sm mb-6" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.8)" }}>
            Pick something memorable. Minimum 6 characters.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <input type="password" placeholder="Current password" className="brutalist-input" value={form.current_password} onChange={update("current_password")} required data-testid="cp-current" />
            <input type="password" placeholder="New password" className="brutalist-input" value={form.new_password} onChange={update("new_password")} required data-testid="cp-new" />
            <input type="password" placeholder="Confirm new password" className="brutalist-input" value={form.confirm_password} onChange={update("confirm_password")} required data-testid="cp-confirm" />
            {error && <p className="text-sm text-red-200 font-semibold" data-testid="cp-error">{error}</p>}
            {success && <p className="text-sm font-semibold" style={{ color: "#FFC2E8" }} data-testid="cp-success">✓ Password updated successfully.</p>}
            <button type="submit" className="brutalist-btn brutalist-btn-pink w-full" disabled={loading} data-testid="cp-submit">
              {loading ? "Saving…" : "Update Password"} <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════ LEAVE HUB CONFIRMATION MODAL ═══════════════ */
const LeaveHubModal = ({ onClose }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center px-6"
    style={{ background: "rgba(0,0,0,0.85)" }}
    onClick={onClose}
    data-testid="leave-hub-modal"
  >
    <div
      className="w-full max-w-md relative text-center"
      style={{ background: "#FC4BAF", border: "3px solid #FC4BAF", boxShadow: "10px 10px 0px 0px #5170FF" }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-white/80 hover:text-white"
        style={{ background: "none", border: "none", cursor: "pointer" }}
        data-testid="leave-hub-close"
      >
        <X size={20} />
      </button>
      <div className="p-8 pt-10">
        <Home size={32} color="#fff" style={{ margin: "0 auto 1rem" }} />
        <h2 style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.4rem", textTransform: "uppercase", color: "#fff", marginBottom: "0.75rem" }}>
          You're leaving the Hub
        </h2>
        <p className="text-sm mb-6" style={{ fontFamily: "Outfit", color: "#fff" }}>
          Click below to continue to Creative Ensemble Media HOME.
        </p>
        <a
          href="/"
          className="brutalist-btn brutalist-btn-pink inline-flex items-center gap-2"
          style={{ background: "#fff", color: "#FC4BAF", borderColor: "#fff" }}
          data-testid="leave-hub-confirm"
        >
          Continue to Home <ArrowRight size={16} />
        </a>
        <button
          onClick={onClose}
          className="block w-full mt-4 text-xs uppercase tracking-wider"
          style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.85)", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
          data-testid="leave-hub-cancel"
        >
          Stay in the Hub
        </button>
      </div>
    </div>
  </div>
);

/* ═══════════════ MAIN HUB PAGE ═══════════════ */
export const HubPage = ({ user, onLogout }) => {
  const [activeSection, setActiveSection] = useState(null); // "discover" | "create" | "amplify" | null
  const [activeRoom, setActiveRoom] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [showLeave, setShowLeave] = useState(false);

  const goHome = () => { setActiveSection(null); setActiveRoom(null); };
  const goSection = (key) => { setActiveSection(key); setActiveRoom(null); };
  const openResources = () => { setActiveSection(null); setActiveRoom(RESOURCES_TILE); };
  const openRoomFromNav = (room) => { setActiveSection(null); setActiveRoom(room); };

  // Persistent header + top nav rendered on every Hub view
  const Shell = ({ children, showBack, onBack, backLabel }) => (
    <div className="min-h-screen" style={{ background: "#000" }}>
      <header className="px-6 py-4 flex items-center justify-between" style={{ background: "#000", borderBottom: "3px solid #FC4BAF" }}>
        <button onClick={goHome} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }} data-testid="hub-home-link">
          <span style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1rem", color: "#fff", textTransform: "uppercase" }}>
            <span style={{ color: "#FC4BAF" }}>Creative</span> <span style={{ color: "#5170FF" }}>Ensemble</span> <span style={{ color: "#FFC2E8" }}>Media</span> <span style={{ color: "#fff" }}>Hub</span>
          </span>
        </button>
        <div className="flex items-center gap-4">
          {showBack && (
            <button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider" style={{ fontFamily: "Outfit", color: "#FFC2E8", background: "none", border: "none", cursor: "pointer" }} data-testid="hub-back-btn">
              <ArrowLeft size={16} /> {backLabel || "Back"}
            </button>
          )}
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); setShowLeave(true); }}
            className="flex items-center gap-1 text-xs uppercase tracking-wider font-semibold hover:text-white transition-colors"
            style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.85)", textDecoration: "none", cursor: "pointer" }}
            data-testid="hub-home-link-main"
          >
            <Home size={14} /> Home
          </a>
          <span className="text-xs text-white/60 hidden md:inline" style={{ fontFamily: "Outfit" }}>{user?.name || user?.email}</span>
          <button
            onClick={() => setShowPwd(true)}
            className="text-white/60 hover:text-white transition-colors"
            data-testid="hub-change-password-btn"
            title="Change password"
          >
            <KeyRound size={18} />
          </button>
          <button onClick={onLogout} className="text-white/60 hover:text-white transition-colors" data-testid="hub-logout">
            <LogOut size={18} />
          </button>
        </div>
      </header>
      <TopNav onSection={goSection} onRoom={openRoomFromNav} onResources={openResources} />
      {children}
      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}
      {showLeave && <LeaveHubModal onClose={() => setShowLeave(false)} />}
    </div>
  );

  // Drill-down: room detail open
  if (activeRoom) {
    const back = activeSection ? () => setActiveRoom(null) : goHome;
    return (
      <Shell showBack onBack={back} backLabel={activeSection ? `Back to ${activeSection}` : "Back to Hub"}>
        <RoomDetailBody room={activeRoom} onResources={openResources} />
      </Shell>
    );
  }

  // Section page open
  if (activeSection) {
    return (
      <Shell showBack onBack={goHome} backLabel="Back to Hub">
        <SectionPageBody sectionKey={activeSection} onSelectRoom={setActiveRoom} />
      </Shell>
    );
  }

  // Main hub view
  const sectionButtons = [
    { key: "discover", label: "Discover", icon: Compass },
    { key: "create",   label: "Create",   icon: Paintbrush },
    { key: "amplify",  label: "Amplify",  icon: Megaphone },
  ];

  return (
    <Shell>

      {/* Welcome */}
      <div className="px-6 pt-12 pb-6 text-center">
        <h1 className="mb-3" style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "clamp(1.5rem, 4vw, 2.5rem)", textTransform: "uppercase" }}>
          <span style={{ color: "#FC4BAF" }}>Creative</span> <span style={{ color: "#5170FF" }}>Ensemble</span> <span style={{ color: "#FFC2E8" }}>Media</span> <span style={{ color: "#fff" }}>Hub</span>
        </h1>
        <p className="text-sm max-w-lg mx-auto font-medium" style={{ fontFamily: "Outfit", color: "#fff" }}>
          The Creative Ensemble Media Hub is where ideas move from concept through production, publishing, and distribution. Explore studio services, submit projects, access Ink + Print resources, and follow how stories develop across our platforms.
        </p>
      </div>

      {/* Marquee — between paragraph and boxes */}
      <div className="marquee-section">
        <Marquee speed={60} gradient={false}>
          {[...Array(4)].map((_, i) => (
            <span key={`hub-marquee-${i}`} className="marquee-text">
              14+ BRANDS&nbsp;&middot;&nbsp;25K+ FOLLOWERS&nbsp;&middot;&nbsp;FILM&nbsp;&middot;&nbsp;PODCASTS&nbsp;&middot;&nbsp;PUBLISHING&nbsp;&middot;&nbsp;DIGITAL STORYTELLING&nbsp;&middot;&nbsp;BRANDED COLLABORATIONS&nbsp;&middot;&nbsp;CREATIVE STRATEGY&nbsp;&middot;&nbsp;COMMUNITY PROJECTS&nbsp;&middot;&nbsp;
            </span>
          ))}
        </Marquee>
      </div>

      {/* Three section buttons (Discover / Create / Amplify) */}
      <div className="max-w-5xl mx-auto px-6 pt-12 pb-6">
        <div className="grid md:grid-cols-3 gap-4" data-testid="hub-sections-grid">
          {sectionButtons.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => goSection(key)}
              className="text-left p-6 transition-all duration-200 hover:-translate-y-1"
              style={{ background: "#5170FF", border: "none", cursor: "pointer", minHeight: "200px" }}
              data-testid={`hub-section-${key}`}
            >
              <Icon size={28} color="#FFC2E8" className="mb-4" />
              <h2 style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.6rem", textTransform: "uppercase", color: "#fff", marginBottom: "0.5rem" }}>
                {label}
              </h2>
              <p className="text-sm mb-4" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.85)" }}>
                {key === "discover" && "Submit pitches, scripts, and projects ready for acquisition."}
                {key === "create"   && "Build your brand, partner with us, and create together."}
                {key === "amplify"  && "Publish, distribute, and reach our 25K+ audience."}
              </p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider" style={{ fontFamily: "Outfit", color: "#FFC2E8" }}>
                Enter <ArrowRight size={12} />
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Resources — separated */}
      <div className="max-w-5xl mx-auto px-6 pb-16">
        <div className="flex items-center gap-3 mb-4 px-1">
          <ShoppingBag size={20} color="#FFC2E8" />
          <h2 style={{ fontFamily: "Garet", fontWeight: 900, fontSize: "1.2rem", textTransform: "uppercase", color: "#FFC2E8" }}>Resources</h2>
        </div>
        <button
          onClick={openResources}
          className="text-left p-5 transition-all duration-200 hover:-translate-y-1 w-full md:w-auto"
          style={{ background: "#FC4BAF", border: "none", cursor: "pointer" }}
          data-testid="room-tile-resources"
        >
          <Download size={16} color="#FFC2E8" className="mb-2" />
          <h3 className="text-sm mb-1" style={{ fontFamily: "Garet", fontWeight: 900, textTransform: "uppercase", color: "#fff" }}>Resources</h3>
          <p className="text-xs" style={{ fontFamily: "Outfit", color: "rgba(255,255,255,0.7)" }}>Guides, templates & creative tools</p>
        </button>
      </div>

      <footer className="py-6 px-6 text-center" style={{ borderTop: "1px solid #222" }}>
        <p className="text-xs text-white/30" style={{ fontFamily: "Outfit" }}>
          &copy; {new Date().getFullYear()} Creative Ensemble Media Hub
        </p>
      </footer>
    </Shell>
  );
};
